import React, { useState, useEffect } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import {
  Button,
  Grid,
  LinearProgress,
  Tooltip,
} from '@material-ui/core';
import { People } from '@material-ui/icons';
import clsx from 'clsx';
import { NetworkStatus, useQuery } from '@apollo/client';
import _ from 'lodash';
import { useHistory, NavLink } from 'react-router-dom';
import { VirtualizedTable } from 'components/Table/VirtualizedTable';
import { PageEmpty, PageHeader, PageSearch, PAGE_KIT, usePageStyles } from 'components/Page/PageKit';
import {CharacterFeedVars,CharacterFeedQuery,CharacterFeedData} from '@pvplevelup/core/dist/queries/character/characterFeed'
import { Waypoint } from 'react-waypoint';
import { classImages } from 'utilities/ClassImages';
import { ClassColorsObject } from 'utilities/team_colors';
import { AocClasses } from '@pvplevelup/core/dist/interfaces/ICharacter';

/** The class filter, grouped by archetype. */
const CLASS_GROUPS: {label: string, classes: AocClasses[]}[] = [
  {label: 'Soldiers', classes: [AocClasses.Guardian, AocClasses.Conqueror, AocClasses.DarkTemplar]},
  {label: 'Rogues', classes: [AocClasses.Assassin, AocClasses.Barbarian, AocClasses.Ranger]},
  {label: 'Priests', classes: [AocClasses.PriestofMitra, AocClasses.BearShaman, AocClasses.TempestofSet]},
  {label: 'Mages', classes: [AocClasses.Demonologist, AocClasses.HeraldofXotli, AocClasses.Necromancer]},
];

/** How long typing has to pause before the list is searched. */
const SEARCH_DELAY = 300;

const HEADER_HEIGHT = 40;

const classColor = (charClass?: string) => (charClass && (ClassColorsObject as Record<string, string>)[charClass]) || '#9e9e9e';

// note: Electron 8 (Chromium 80) has no flexbox gap; spacing here is grid gap or margins
const useStyles = makeStyles((theme) => ({
  /** class filter on the left, the active count and clear button on the right */
  filterRow: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1fr) auto',
    gridGap: theme.spacing(2),
    alignItems: 'end',
  },
  classGroups: {
    display: 'flex',
    flexWrap: 'wrap',
    marginBottom: -theme.spacing(1),
  },
  classGroupBox: {
    marginRight: theme.spacing(3),
    marginBottom: theme.spacing(1),
  },
  filterLabel: {
    fontSize: '0.68rem',
    fontWeight: 800,
    letterSpacing: '0.12em',
    textTransform: 'uppercase',
    color: PAGE_KIT.faint,
    marginBottom: theme.spacing(0.75),
  },
  classGroup: {
    display: 'grid',
    gridAutoFlow: 'column',
    gridAutoColumns: 'max-content',
    gridGap: theme.spacing(0.75),
  },
  classToggle: {
    width: 42,
    height: 42,
    boxSizing: 'border-box',
    borderRadius: 10,
    border: '1px solid rgba(255, 255, 255, 0.09)',
    background: 'rgba(255, 255, 255, 0.03)',
    display: 'grid',
    placeItems: 'center',
    cursor: 'pointer',
    transition: 'background 0.15s, border-color 0.15s, box-shadow 0.15s, transform 0.1s',
    '&:hover': {
      background: 'rgba(255, 255, 255, 0.08)',
      borderColor: 'rgba(255, 255, 255, 0.18)',
    },
    '&:active': {
      transform: 'scale(0.95)',
    },
    '& img': {
      width: 28,
      height: 28,
      transition: 'opacity 0.15s',
    },
  },
  filterSide: {
    display: 'grid',
    gridAutoFlow: 'column',
    gridGap: theme.spacing(1.25),
    alignItems: 'center',
    marginBottom: 2,
  },
  activePill: {
    color: '#90caf9',
    background: 'rgba(66, 165, 245, 0.14)',
    border: '1px solid rgba(66, 165, 245, 0.3)',
  },
  clearButton: {
    height: 34,
    padding: '0 14px',
    borderRadius: 9,
    textTransform: 'none',
    fontWeight: 700,
    whiteSpace: 'nowrap',
    color: 'rgba(255, 255, 255, 0.85)',
    background: 'rgba(255, 255, 255, 0.06)',
    border: '1px solid rgba(255, 255, 255, 0.1)',
    '&:hover': {
      color: '#fff',
      background: 'rgba(255, 255, 255, 0.12)',
    },
    '&.Mui-disabled': {
      color: 'rgba(255, 255, 255, 0.3)',
      background: 'transparent',
      borderColor: 'rgba(255, 255, 255, 0.06)',
    },
  },
  nameCell: {
    display: 'grid',
    gridTemplateColumns: 'auto minmax(0, 1fr)',
    gridGap: theme.spacing(1.5),
    alignItems: 'center',
    minWidth: 0,
    width: '100%',
  },
  classTile: {
    width: 38,
    height: 38,
    boxSizing: 'border-box',
    borderRadius: 10,
    display: 'grid',
    placeItems: 'center',
    border: '1px solid rgba(255, 255, 255, 0.1)',
    '& img': {
      width: 26,
      height: 26,
    },
  },
  name: {
    display: 'block',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    fontSize: '0.95rem',
  },
  className: {
    marginTop: 1,
    fontSize: '0.75rem',
    fontWeight: 600,
    letterSpacing: '0.02em',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  level: {
    fontWeight: 800,
    fontSize: '0.95rem',
    fontVariantNumeric: 'tabular-nums',
  },
  guild: {
    fontWeight: 600,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  faint: {
    color: PAGE_KIT.faint,
    fontSize: '0.85em',
  },
  emptyOverlay: {
    position: 'absolute',
    top: HEADER_HEIGHT,
    left: 0,
    right: 0,
  },
  loadingMore: {
    position: 'absolute',
    bottom: 12,
    left: '50%',
    transform: 'translateX(-50%)',
    padding: '5px 14px',
    borderRadius: 14,
    fontSize: '0.8rem',
    fontWeight: 700,
    whiteSpace: 'nowrap',
    color: '#90caf9',
    background: 'rgba(18, 18, 22, 0.92)',
    border: '1px solid rgba(66, 165, 245, 0.3)',
    boxShadow: '0 6px 16px rgba(0, 0, 0, 0.35)',
  },
}));

interface Props {}

/** Every character the app has seen: search by name, filter by class, open one for its profile. */
export const CharacterList: React.FC<Props> = (props) => {
  const history = useHistory();
  const classes = useStyles();
  const page = usePageStyles();

  // what's typed, and what the list is searched for once typing pauses
  const [ searchText, setSearchText ] = useState('');
  const [ search, setSearch ] = useState('');
  const [ selectedClasses, setSelectedClasses ] = useState<AocClasses[]>([]);

  useEffect(() => {
    const timer = setTimeout(() => setSearch(searchText.trim()), SEARCH_DELAY);
    return () => clearTimeout(timer);
  }, [searchText]);

  // a new search or class filter is a new query: Apollo fetches it from the first page
  const { data, loading, networkStatus, fetchMore } = useQuery<CharacterFeedData, CharacterFeedVars>(CharacterFeedQuery, {
    variables: {next: "", search, classes: selectedClasses},
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
  });

  const characters = data?.characterFeed?.characters || [];
  const hasNext = !!data?.characterFeed?.hasNext;
  const loadingMore = networkStatus == NetworkStatus.fetchMore;

  const loadMore = () => {
    if(!hasNext || loading)
      return;
    fetchMore({
      variables: {next: data?.characterFeed.next, search, classes: selectedClasses},
      updateQuery: (prev, {fetchMoreResult}) => {
        if (!fetchMoreResult) return prev;
        return Object.assign({}, prev, {
          characterFeed: {
            ...fetchMoreResult.characterFeed,
            characters: _.uniqBy([...(prev?.characterFeed?.characters || []), ...fetchMoreResult.characterFeed.characters], (c) => c._id),
          }
        });
      }
    });
  }

  const toggleClass = (charClass: AocClasses) => {
    setSelectedClasses((current) => current.indexOf(charClass) != -1 ? current.filter((c) => c != charClass) : current.concat(charClass));
  }

  const clearFilters = () => {
    setSearchText('');
    setSearch('');
    setSelectedClasses([]);
  }

  const filtered = selectedClasses.length > 0 || searchText != '';

  return (
    <Grid container spacing={2} className="content-container">
      <Grid item xs={12}>
        <PageHeader
          title="Characters"
          subtitle="Every character the app has seen. Search by name or filter by class."
          icon={<People />}
          actions={<PageSearch value={searchText} onChange={setSearchText} placeholder="Search characters by name..." width={320} />}
          childrenStyle="filters"
        >
          <div className={classes.filterRow}>
            <div className={classes.classGroups}>
              {CLASS_GROUPS.map((group) => (
                <div key={group.label} className={classes.classGroupBox}>
                  <div className={classes.filterLabel}>{group.label}</div>
                  <div className={classes.classGroup}>
                    {group.classes.map((charClass) => {
                      const selected = selectedClasses.indexOf(charClass) != -1;
                      const color = classColor(charClass);
                      return (
                        <Tooltip key={charClass} title={charClass}>
                          <div
                            className={classes.classToggle}
                            onClick={() => toggleClass(charClass)}
                            style={selected ? {borderColor: color, background: `${color}2e`, boxShadow: `inset 0 0 0 1px ${color}, 0 0 14px ${color}40`} : undefined}
                          >
                            <img src={selected ? classImages.getColorIcon(charClass) : classImages.getStandardIcon(charClass)} style={{opacity: selected ? 1 : 0.65}} />
                          </div>
                        </Tooltip>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
            <div className={classes.filterSide}>
              {selectedClasses.length > 0 &&
                <span className={clsx(page.pill, classes.activePill)}>{selectedClasses.length} {selectedClasses.length == 1 ? 'class' : 'classes'}</span>
              }
              <Button className={classes.clearButton} disabled={!filtered} onClick={clearFilters}>Clear filters</Button>
            </div>
          </div>
        </PageHeader>
      </Grid>

      <Grid item xs={12}>
        <div className={page.tablePanel} style={{height: 'calc(97vh - 300px)', minHeight: 320}}>
          {loading && <LinearProgress style={{position: 'absolute', top: 0, left: 0, right: 0, zIndex: 2, height: 2}} />}
          <VirtualizedTable
            rowHeight={56}
            headerHeight={HEADER_HEIGHT}
            rowCount={characters.length}
            rowGetter={({ index }) => characters[index]}
            onRowClick={({rowData}) => rowData?._id && history.push(`/characters/${rowData._id}`)}
            customCellRenderer={({columnIndex,rowIndex,cellData,rowData})=>{
              const color = classColor(rowData.class);
              return (<React.Fragment>
                {columnIndex == 0 ?
                  <div className={classes.nameCell}>
                    <div className={classes.classTile} style={{background: `${color}1f`, borderColor: `${color}55`}} title={rowData.class}>
                      <img src={classImages.getColorIcon(rowData.class as AocClasses)} />
                    </div>
                    <div style={{minWidth: 0}}>
                      <NavLink to={`/characters/${rowData._id}`} className={clsx(page.link, classes.name)} onClick={(e) => e.stopPropagation()}>{cellData}</NavLink>
                      <div className={classes.className} style={{color}}>{rowData.class || 'Unknown class'}</div>
                    </div>
                  </div>
                : columnIndex == 1 ?
                  (cellData != null && cellData !== '' ? <span className={classes.level}>{cellData}</span> : <span className={classes.faint}>-</span>)
                : columnIndex == 2 ?
                  (cellData ? <span className={page.pill}>{cellData}</span> : <span className={classes.faint}>-</span>)
                : columnIndex == 3 ?
                  (cellData ? <span className={classes.guild}>{cellData}</span> : <span className={classes.faint}>No guild</span>)
                :
                  (cellData != null && cellData !== '' ? cellData : <span className={classes.faint}>-</span>)
                }
                {(columnIndex == 0 && rowIndex == characters.length - 3) &&
                  <Waypoint onEnter={loadMore} />
                }
              </React.Fragment>)
            }}
            columns={[
              {
                width: 260,
                label: 'Character',
                dataKey: 'name'
              },
              {
                width: 80,
                label: 'Level',
                dataKey: 'level',
                numeric: true,
              },
              {
                width: 100,
                label: 'Server',
                dataKey: 'server',
              },
              {
                width: 220,
                label: 'Guild',
                dataKey: 'guild'
              }
            ]}
          />
          {!loading && characters.length == 0 &&
            <div className={classes.emptyOverlay}>
              <PageEmpty
                icon={<People />}
                title={search ? `No characters named like "${search}"` : filtered ? 'No characters match these filters' : 'No characters yet'}
                text={search && selectedClasses.length > 0 ? 'Nothing matches that name in the selected classes.' : filtered ? 'Try a different name or fewer classes.' : 'Characters show up here once the app has seen them.'}
                action={filtered ? <Button className={page.secondaryButton} onClick={clearFilters}>Clear filters</Button> : undefined}
              />
            </div>
          }
          {loadingMore && <div className={classes.loadingMore}>Loading more...</div>}
        </div>
      </Grid>
    </Grid>
  );
};
